import {useEffect, useRef, useState} from 'react';
import {View, Text, StyleSheet, Animated} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import {CircleCheck} from 'lucide-react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {useUploadState} from '@/contexts/UploadContext';

export const UploadProgressToast = () => {
    const {isUploading, progress, fileName} = useUploadState();
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);

    const animValue = useRef(new Animated.Value(0)).current;
    const [visible, setVisible] = useState(false);
    const visibleRef = useRef(false);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (isUploading) {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            setVisible(true);
            visibleRef.current = true;
            Animated.timing(animValue, {
                toValue: 1,
                duration: 300,
                useNativeDriver: true,
            }).start();
        } else if (visibleRef.current) {
            hideTimerRef.current = setTimeout(() => {
                Animated.timing(animValue, {
                    toValue: 0,
                    duration: 300,
                    useNativeDriver: true,
                }).start(({finished}) => {
                    if (finished) {
                        setVisible(false);
                        visibleRef.current = false;
                    }
                });
            }, 2000);
        }

        return () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
            }
        };
    }, [isUploading, animValue]);

    if (!visible) return null;

    const isComplete = !isUploading && progress === 1;
    const isIndeterminate = progress === -1;
    const displayProgress = isIndeterminate ? 0 : Math.round((progress ?? 0) * 100);

    const translateY = animValue.interpolate({
        inputRange: [0, 1],
        outputRange: [50, 0],
    });

    return (
        <Animated.View
            style={[
                styles.container,
                {
                    opacity: animValue,
                    transform: [{translateY}],
                },
            ]}
            pointerEvents={isUploading ? 'auto' : 'none'}
        >
            <View style={styles.content}>
                {isComplete ? (
                    <View style={styles.textRow}>
                        <CircleCheck size={18} color={theme.colors.primary} />
                        <Text style={styles.successText}>
                            {t('media.sharedFolders.uploadSuccess')}
                        </Text>
                    </View>
                ) : (
                    <>
                        <View style={styles.textRow}>
                            <Text style={styles.fileName} numberOfLines={1}>
                                {fileName || t('media.sharedFolders.uploading')}
                            </Text>
                            <Text style={styles.percentage}>
                                {isIndeterminate
                                    ? t('media.sharedFolders.uploading')
                                    : t('media.sharedFolders.uploadProgress', {progress: displayProgress})}
                            </Text>
                        </View>
                        <View style={styles.progressTrack}>
                            {isIndeterminate ? (
                                <View style={[styles.progressBar, {width: '100%', opacity: 0.5}]} />
                            ) : (
                                <View style={[styles.progressBar, {width: `${displayProgress}%`}]} />
                            )}
                        </View>
                    </>
                )}
            </View>
        </Animated.View>
    );
};

const createStyles = (theme: ThemeType, insets: {bottom: number}) =>
    StyleSheet.create({
        container: {
            position: 'absolute',
            bottom: insets.bottom + 16,
            left: 16,
            right: 16,
            zIndex: 20,
        },
        content: {
            backgroundColor: theme.colors.surface + 'F0',
            borderRadius: theme.borderRadius.lg,
            padding: 14,
            gap: 8,
        },
        textRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        fileName: {
            flex: 1,
            fontSize: theme.fontSize.sm,
            color: theme.colors.text,
            fontWeight: '500',
            marginRight: 8,
        },
        percentage: {
            fontSize: theme.fontSize.xs,
            color: theme.colors.textSecondary,
        },
        successText: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.primary,
            fontWeight: '600',
            marginLeft: 8,
        },
        progressTrack: {
            height: 4,
            backgroundColor: theme.colors.border,
            borderRadius: 2,
            overflow: 'hidden',
        },
        progressBar: {
            height: '100%',
            backgroundColor: theme.colors.primary,
            borderRadius: 2,
        },
    });
